import { FolderContinuousRecordStore } from './folder-continuous-record.store';
import { fb, translate } from '../../../../../modules/shared/shared.global-service';
import { Component, effect, HostListener, inject, viewChild, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { ContextMenu, ContextMenuModule } from '@openng/optimus-ui/contextmenu';
import { ListboxChangeEvent, ListboxModule } from '@openng/optimus-ui/listbox';
import { MenuItem, PrimeTemplate } from '@openng/optimus-ui/api';
import { TranslatePipe } from '@ngx-translate/core';
import { UserChipComponent } from '../../../../kit/user-chip/user-chip.component';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { IOption } from '../../../../../modules/shared/components/inputs/input-fulltext/input-fulltext.component';
import { FilterBuilder } from '../../../../../modules/content/helper/filter.builder';
import { WorkGridItemObject } from '@it2go/types';
import {
    WorkDataContinuousRecordObject,
} from '@libs/types/src/services/object-type/work/work-data-continuous-record.object';
import { ServicePatternStructureUsageEnum } from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import { FormsModule, Validators } from '@angular/forms';
import { workDataFrag } from './folder-continuous-record.query';
import { WorkContinuousRecordComponent } from '../../../work/work-continuous-record/work-continuous-record.component';
import { WorkOfferUpsertInput } from '@libs/types/src/services/input-type/work/work-offer.upsert-input';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import { isInvalid } from '../../../../../modules/shared/helpers/validate';
import { InputFulltextComponent } from '../../../../../modules/shared/components/inputs/input-fulltext/input-fulltext.component';
import { WorkPrintService } from '../../../../medical-service/work/work-print.service';

@Component({
    selector: 'app-folder-continuous-record',
    templateUrl: './folder-continuous-record.component.html',
    styleUrl: './folder-continuous-record.component.scss',
    providers: [FolderContinuousRecordStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        ContextMenuModule,
        ListboxModule,
        PrimeTemplate,
        TranslatePipe,
        UserChipComponent,
        FormsModule,
        WorkContinuousRecordComponent,
        InputFulltextComponent,
    ],
})
export class FolderContinuousRecordComponent {

    public readonly store = inject(FolderContinuousRecordStore);
    protected readonly folderStore = inject(FolderGlobalStore);
    protected readonly workPrint = inject(WorkPrintService);

    public readonly continuousRecordFormComponent = viewChild<WorkContinuousRecordComponent>('continuousRecordForm');

    public selectWorkData: WorkGridItemObject<WorkDataContinuousRecordObject> | null = null;

    protected form = fb.group({
        search: fb.control<string>(''),
        selectedWorkData: fb.control<string>('', { nonNullable: true }),
        dateFrom: fb.control<Date>(new Date(), Validators.required),
        offers: fb.control<WorkOfferUpsertInput[]>([]),
    });

    @ViewChild('contextMenu') ctx!: ContextMenu;
    protected contextMenuItemsAll: MenuItem[] = [
        {
            label: translate.instant('common.update'),
            icon: 'pi pi-file-edit', command: () => this.update(),
        },
        {
            label: translate.instant('common.delete'),
            icon: 'pi pi-times', command: () => this.delete(),
        },
    ];
    protected contextMenuItems = this.contextMenuItemsAll;
    private contextItem: number = 0;

    constructor() {
        this.store.onSuccess(
            () => this.fetchData(),
            'createWork',
            'updateWork',
            'deleteWork',
        );

        effect(() => {
            this.fetchData();
        });

        effect(() => {
            const workRecords = this.store.workRecords();

            if (workRecords.length > 0) {
                this.selectWorkData = workRecords[0];
                if (this.selectWorkData) {
                    this.form.patchValue({
                        selectedWorkData: this.selectWorkData.workData?.data || '',
                    });
                    this.continuousRecordFormComponent()?.setData(this.selectWorkData);
                } else {
                    this.form.reset();
                }
            }
        });
    }

    public listChange(index: ListboxChangeEvent): void {
        if (index.value) {
            this.selectWorkData = this.store.workRecords()[index.value.index];
            this.continuousRecordFormComponent()?.setData(this.selectWorkData);
        }
    }

    @HostListener('document:contextmenu')
    public rightClickAny() {
        this.ctx.hide();
    }

    @HostListener('document:click')
    public leftClickAny() {
        this.ctx.hide();
    }

    public rightClick(menu: ContextMenu, event: MouseEvent, item: any): void {
        this.contextItem = item.id;
        menu.hide();
        event.preventDefault();
        this.contextMenuItems = this.contextMenuItemsAll.slice(this.selectWorkData?.id === item.id ? 0 : 1);
        setTimeout(() => {
            menu.toggle(event);
        }, 1);
    }

    protected workDataChange(workDataSearch: IOption | null): void {
        this.selectWorkData = workDataSearch as any;
        this.form.patchValue({ selectedWorkData: this.selectWorkData?.workData?.data ?? '' });
    }

    protected async delete() {
        if (this.contextItem) {
            await this.store.api.deleteWork({ id: this.contextItem });
            contentGlobalSuccess.next('common.deleted');
        }
    }

    protected async update() {
        if (this.contextItem) {
            const form = this.continuousRecordFormComponent()!.form;
            if (isInvalid(form)) return;
            await this.store.api.updateWork({
                id: this.contextItem,
                offers: form.value.offers || [],
                dateFrom: form.value.dateFrom?.toISOString(),
                tags: form.value.tags || [],
                workData: JSON.stringify({
                    data: form.value.data || '',
                    form: form.value.form,
                    presence: form.value.presence,
                }),
            });
            contentGlobalSuccess.next('common.updated');
        }
    }

    protected print(): void {
        if (this.selectWorkData) this.workPrint.openRow(this.selectWorkData);
    }

    private fetchData() {
        if (!this.folderStore.folder()?.serviceId) return;
        this.store.api.getWorks(FilterBuilder.singlePage({
            folderId: this.folderStore.folderId(),
            workType: WorkTypeEnum.CONTINUOUS_RECORD,
            sort: { column: 'dateFrom', direction: 'DESC' },
        }), workDataFrag).then();
    }

    protected readonly ServicePatternStructureUsageEnum = ServicePatternStructureUsageEnum;

}
